import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { LogoBlock } from "../LogoBlock/LogoBlock";
import { reportThemes as themes } from "../Themes";
import { BackgroundBlocks } from "../BackgroundBlocks/BackgroundBlocks";
import { FooterBlock } from "../FooterBlock/FooterBlock";
import { Body } from "../Body/Body";
import { BodyHeader, BodyHeaderItem } from "../BodyHeader/BodyHeader";
import { Table } from "../Table/Table";
import { CheckBoxContainer } from "../CheckBoxContainer/CheckBoxContainer";
import { TextArea } from "../TextArea/TextArea";
import generalStyle from "../GeneralStyles";
import { decimalPlaces, roundMealValue } from "../../../../helpers/mealsHelper";

const weeks = ["One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight"];

const headersTable1 = [
  "Data Range Average",
  "Complete",
  "Protein",
  "Carb",
  "Simple Sugar",
];

const headersTable2 = [
  "Average",
  "Complete",
  "Protein",
  "Carb",
  "Simple Sugar",
];

const checkBoxesProps1 = {
  text: "Nutrition Optimized:",
  options: ["Yes", "No"],
};

const checkBoxesProps2 = {
  text: "Requirement of Nutrition Stabilization is met:",
  options: ["Yes", "No"],
};

const nutritionStyle = StyleSheet.create({
  note: {
    ...generalStyle.bold,
    fontSize: 8,
    marginTop: 7,
  },
  text: {
    fontSize: 10,
  },
  defaultMargin: {
    marginTop: 10,
  },
  signature: {
    marginLeft: 2,
  },
  signtureTitle: {
    marginLeft: 5,
  },
  weekSentece: {
    marginLeft: 4,
  },
});

const NutritionStabilizationReport = (data, { theme = "rarify" }) => {
  const { participantID, generatedDate, currentStudy } = data;
  const totalWeeks = data.totalWeeks;
  const totalPerWeek = data.totalPerWeek;

  const themeData = themes[theme];

  return (
    <Document data-testid="nutrition-report">
      <Page size="A4" style={generalStyle.page}>
        <View fixed>
          <BackgroundBlocks {...{ themeData }} />
          <LogoBlock {...{ themeData }} />
        </View>
        <Body
          {...{ themeData }}
          title="Verification of Nutrition Stabilization"
        >
          <BodyHeader {...{ themeData }}>
            <BodyHeaderItem
              title="Protocol Number:"
              subTitle={currentStudy.toUpperCase()}
            />
            <BodyHeaderItem title="Participant ID:" subTitle={participantID} />
          </BodyHeader>
          <Table headers={headersTable1} {...{ themeData }}>
            <View>
              <Text
                style={{
                  ...generalStyle.semiBold,
                  ...nutritionStyle.text,
                }}
              >
                {totalWeeks.dateRange}
              </Text>
              <Text
                style={{
                  ...nutritionStyle.text,
                  ...generalStyle.semiBold,
                }}
              >
                {totalWeeks.completed} day(s)
              </Text>
              <Text
                style={{
                  ...nutritionStyle.text,
                }}
              >
                {Number(roundMealValue(totalWeeks.protein.percentage)).toFixed(
                  decimalPlaces
                )}{" "}
                %
              </Text>
              <Text
                style={{
                  ...nutritionStyle.text,
                }}
              >
                {Number(
                  roundMealValue(totalWeeks.carbohydrates.percentage)
                ).toFixed(decimalPlaces)}{" "}
                %
              </Text>
              <Text
                style={{
                  ...nutritionStyle.text,
                }}
              >
                {Number(roundMealValue(totalWeeks.sugars.value)).toFixed(
                  decimalPlaces
                )}{" "}
                g
              </Text>
            </View>
          </Table>
          <Table headers={headersTable2} {...{ themeData }}>
            {totalPerWeek.map((row, index) => (
              <View key={index}>
                <View style={generalStyle.inLineBlocks}>
                  <Text
                    style={{
                      ...generalStyle.bold,
                      ...nutritionStyle.text,
                    }}
                  >
                    Week {weeks[index]}
                  </Text>
                  <Text
                    style={{
                      ...generalStyle.semiBold,
                      ...nutritionStyle.text,
                      ...nutritionStyle.weekSentece,
                    }}
                  >
                    {row.week}
                  </Text>
                </View>

                <Text
                  style={{
                    ...nutritionStyle.text,
                    ...generalStyle.semiBold,
                  }}
                >
                  {row.sumPerWeek.completed} day(s)
                </Text>
                <Text
                  style={{
                    ...nutritionStyle.text,
                  }}
                >
                  {Number(
                    roundMealValue(row.sumPerWeek.protein.percentage)
                  ).toFixed(decimalPlaces)}{" "}
                  %
                </Text>
                <Text
                  style={{
                    ...nutritionStyle.text,
                  }}
                >
                  {Number(
                    roundMealValue(row.sumPerWeek.carbohydrates.percentage)
                  ).toFixed(decimalPlaces)}{" "}
                  %
                </Text>
                <Text
                  style={{
                    ...nutritionStyle.text,
                  }}
                >
                  {Number(roundMealValue(row.sumPerWeek.sugars.value)).toFixed(
                    decimalPlaces
                  )}{" "}
                  g
                </Text>
              </View>
            ))}
          </Table>
          <Text style={nutritionStyle.note}>
            *Note: Protein, Carb and Simple Sugars are average of complete days
            in the date range
          </Text>
          <View style={generalStyle.marginTopAuto}>
            <CheckBoxContainer {...checkBoxesProps1} />
            <CheckBoxContainer {...checkBoxesProps2} />
            <TextArea text="Comments by Principal Investigator:" />
            <View
              style={{
                ...generalStyle.inLineBlocks,
                ...nutritionStyle.defaultMargin,
              }}
            >
              <View
                style={{
                  ...generalStyle.inLineBlocks,
                }}
              >
                <Text
                  style={{ ...nutritionStyle.text, ...generalStyle.semiBold }}
                >
                  Date of Verification by Principal Investigator:
                </Text>
                <Text
                  style={{
                    ...nutritionStyle.text,
                    ...nutritionStyle.signature,
                  }}
                >
                  __ __ / __ __ __ / __ __ __ __
                </Text>
              </View>
            </View>
            <View
              style={{
                ...generalStyle.inLineBlocks,
                ...nutritionStyle.defaultMargin,
              }}
            >
              <Text
                style={{
                  ...nutritionStyle.text,
                  ...generalStyle.semiBold,
                }}
              >
                PI Name:
              </Text>
              <Text style={nutritionStyle.text}>
                __________________________________________
              </Text>
              <Text
                style={{
                  ...nutritionStyle.text,
                  ...generalStyle.semiBold,
                  ...nutritionStyle.signature,
                }}
              >
                PI Signature:
              </Text>
              <Text
                style={{ ...nutritionStyle.text, ...nutritionStyle.signature }}
              >
                _________________________________________
              </Text>
            </View>
          </View>
        </Body>
        <FooterBlock fixed {...{ themeData }} {...{ generatedDate }} />
      </Page>
    </Document>
  );
};

export default NutritionStabilizationReport;
